import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { AdminGuard, AdminShell } from "./AdminShell";
import { authHeaders } from "../../context/AuthContext";
import { useSeo } from "../../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEnquiriesPage() {
  useSeo({ title: "Admin · Enquiries", description: "Enquiries inbox.", path: "/admin/enquiries" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <AdminGuard>
      <Toaster position="top-right" richColors />
      <AdminShell>
        <div className="mb-8">
          <div className="eyebrow mb-2">Inbox</div>
          <h1 className="font-display text-3xl tracking-tight">Enquiries</h1>
        </div>
        {loading ? (
          <div className="opacity-60 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-border p-8 text-sm opacity-60">No enquiries yet.</div>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-5 border border-border">
              {items.map((e) => (
                <button
                  key={e.id}
                  data-testid={`admin-enquiry-item-${e.id}`}
                  onClick={() => setSelected(e)}
                  className={`w-full text-left px-4 py-4 border-b border-border last:border-b-0 hover:bg-card transition-colors ${selected?.id === e.id ? "bg-card" : ""}`}
                >
                  <div className="flex justify-between mb-1">
                    <div className="font-display text-base">{e.name}</div>
                    <div className="font-mono-tab text-xs opacity-60">{(e.created_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="text-xs opacity-70 truncate">{e.email}</div>
                  <div className="text-xs opacity-70 mt-1 line-clamp-2">{e.message}</div>
                </button>
              ))}
            </div>
            <div className="col-span-12 md:col-span-7 border border-border p-6 min-h-[320px]">
              {selected ? (
                <div>
                  <div className="eyebrow mb-2">Enquiry detail</div>
                  <h2 className="font-display text-2xl tracking-tight mb-3">{selected.name}</h2>
                  <div className="text-sm opacity-80 grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                    <div><b>Email:</b> {selected.email}</div>
                    <div><b>Company:</b> {selected.company || "—"}</div>
                    <div><b>Phone:</b> {selected.phone || "—"}</div>
                    <div><b>Industry:</b> {selected.industry || "—"}</div>
                    <div><b>Date:</b> {(selected.created_at || "").slice(0, 19).replace("T", " ")}</div>
                  </div>
                  <div className="eyebrow mb-2">Message</div>
                  <div className="border-t border-border pt-4 text-sm whitespace-pre-wrap opacity-90 leading-relaxed">
                    {selected.message}
                  </div>
                  <div className="mt-6">
                    <a href={`mailto:${selected.email}`} className="btn-copper">Reply via email</a>
                  </div>
                </div>
              ) : (
                <div className="opacity-60 text-sm">Select an enquiry to view details.</div>
              )}
            </div>
          </div>
        )}
      </AdminShell>
    </AdminGuard>
  );
}
